import TimelineChart from 'components/TimelineChart/TimelineChart';
import classNames from 'classnames';
import { differenceInMonths } from 'date-fns';
import React from 'react';
import {
    LABELS,
    ROW_HEIGHT,
    TIMELINE_SCROLL_THRESHOLD,
    BASE_CHART_WIDTH,
} from './constants';
import { TimelineData } from '../types';
import './ExecutiveSummaryTimelineChart.scss';

export interface IProps {
    data: TimelineData[];
    enableScrollView?: boolean;
    lightMode?: boolean;
}

const TIMELINE_DATE_FIELDS: Array<keyof TimelineData> = [
    'analysisStart',
    'firstUnitDelivered',
    'developmentStartDate',
    'developmentEndDate',
    'operationStartDate',
    'firstStabilizedMonth',
    'saleMonth',
];

type TimelineContainerStyle = React.CSSProperties & {
    ['--timeline-min-width']?: string;
};

const getTimelineMonthSpan = (timelineData: TimelineData[]): number => {
    let minDate: Date | null = null;
    let maxDate: Date | null = null;

    timelineData.forEach(item => {
        TIMELINE_DATE_FIELDS.forEach(field => {
            const value = item[field];

            if (!value) {
                return;
            }

            const parsedDate = new Date(value);

            if (Number.isNaN(parsedDate.getTime())) {
                return;
            }

            if (!minDate || parsedDate < minDate) {
                minDate = parsedDate;
            }

            if (!maxDate || parsedDate > maxDate) {
                maxDate = parsedDate;
            }
        });
    });

    if (!minDate || !maxDate) {
        return 0;
    }

    return differenceInMonths(maxDate, minDate) + 1;
};

const getTimelineMinWidth = (months: number): number => (
    Math.ceil((months / TIMELINE_SCROLL_THRESHOLD) * BASE_CHART_WIDTH)
);

const ExecutiveSummaryTimelineChart = ({
    data,
    enableScrollView = true,
    lightMode = false,
} : IProps) => {
    const totalMonths = React.useMemo(() => getTimelineMonthSpan(data), [data]);
    const shouldEnableScroll = totalMonths > TIMELINE_SCROLL_THRESHOLD;
    const containerStyle: TimelineContainerStyle | undefined = shouldEnableScroll
        ? { '--timeline-min-width': `${getTimelineMinWidth(totalMonths)}px` }
        : undefined;

    return (
        <div
            className={classNames(
                'executive-summary-timeline-chart_wrapper',
                'mb-3',
                'px-0',
                {
                    'executive-summary-timeline-chart_wrapper--scrollable':
                        shouldEnableScroll,
                },
            )}
        >
            <div
                className={classNames(
                    'executive-summary-timeline-chart_container',
                    {
                        'executive-summary-timeline-chart_container--scrollable':
                            shouldEnableScroll,
                    },
                )}
                style={containerStyle}
            >
                <TimelineChart
                    data={data}
                    scatterXFields={[
                        { name: 'analysisStart', label: LABELS.analysisStart },
                        { name: 'developmentStartDate', label: LABELS.developmentStartDate },
                        { name: 'developmentEndDate', label: LABELS.developmentEndDate },
                        { name: 'operationStartDate', label: LABELS.operationStartDate },
                        { name: 'firstStabilizedMonth', label: LABELS.firstStabilizedMonth },
                        { name: 'saleMonth', label: LABELS.saleMonth },
                    ]}
                    barXFields={{
                        start: 'developmentStartDate',
                        end: 'developmentEndDate',
                        label: LABELS.constructionPeriod,
                    }}
                    enableScrollView={enableScrollView}
                    yField="dealName"
                    hideYAxisLabels
                    rowHeight={ROW_HEIGHT}
                    lightMode={lightMode}
                />
            </div>
        </div>
    );
};

export default ExecutiveSummaryTimelineChart;
